import {
    Button,
    Container,
    Divider,
    InputAdornment,
    Link,
    Skeleton,
    Stack,
    TextField,
    type Theme,
    Typography,
    useMediaQuery,
} from '@mui/material';
import Grid2 from '@mui/material/Unstable_Grid2';
import { useEffect, useMemo, useState } from 'react';
import { type UseFormReturn, useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import Alert from '/@/components/bayer/alert';
import PageContainer from '/@/components/bayer/page-container';
import EnvironmentSliderV2 from '/@/components/environment-slider-v2';
import RecommendationCommentsButton from '/@/components/recommendation-comments-button';
import RecommendationCommentsDrawer from '/@/components/recommendation-comments-drawer';
import RecommendationShare from '/@/components/recommendation-share';
import Spinner from '/@/components/spinner';
import useUnitProvider from '/@/core/context/unit';
import { getCornPlantingDensity } from '/@/core/density';
import useBreadcrumbs from '/@/hooks/breadcrumbs';
import { useCatalogProductById } from '/@/hooks/catalog';
import { useMutateCreateOrUpdateCropMonitoring } from '/@/hooks/crop-monitoring/use-crop-monitoring';
import useDebounce, { useDebounceWithDeepCompare } from '/@/hooks/debounce/useDebounce';
import { useFieldById } from '/@/hooks/fields';
import { useRecommendationById } from '/@/hooks/my-recommendations/useFilterMyRecommendations';
import { useGetUserNode } from '/@/hooks/nodes';
import {
    getClimatePatternChoices,
    useCornDensityCurveForProducts,
    useEnvironments,
    useFertilizationCoefficientsForProducts,
    useRecommendationCommentsPermissions,
    useUpdateRecommendationMutation,
    useYieldByEnvironmentForProducts,
} from '/@/hooks/recommendation';
import { useHasWriteAccess } from '/@/hooks/session';
import { _ } from '/@/locale';
import { getAppInstance, getSessionCountry } from '/@/store/session/selectors';
import type { DensityRecommendationForm } from '/@/types/density-recommendation';
import type { Recommendation } from '/@/types/recommendation';
import { formatDate, objectEntries } from '/@/utils';
import { reverse } from '/@/utils/router';
import Header from '../../../components/bayer/header';
import UserDensityModal, { UserDensityTable } from './components/user-density-modal';

const ValueCard = ({ title, value, loading }: { title: string; value: string; loading: boolean }) => {
    return (
        <Stack spacing={1}>
            <Typography variant='subtitle1' color='primary'>
                {title}
            </Typography>
            <Typography variant='h5' color='primary'>
                {loading ? <Skeleton variant='text' width='100%' /> : value}
            </Typography>
        </Stack>
    );
};

const FertilizationCard = ({
    fertilizationCoefficients,
}: {
    fertilizationCoefficients: ReturnType<typeof useFertilizationCoefficientsForProducts>['data'][number];
}) => {
    const fertilizationCoefficientsToDisplay = ['n', 'p', 'k', 's'];
    const fertilization =
        objectEntries(fertilizationCoefficients ?? {})
            ?.filter(([key]) => fertilizationCoefficientsToDisplay.includes(key))
            ?.map(([key, value]) => {
                const valueToDisplay = value?.recommended || value?.common || value;
                return {
                    name: key.toUpperCase(),
                    value: valueToDisplay ? `${valueToDisplay?.toFixed(2)} kg/ha` : '-',
                };
            }) ?? [];

    return (
        <Stack spacing={0.5}>
            <Typography variant='subtitle2' color='primary' fontWeight={'bold'}>
                {_('Fertilizantes necesarios')}
            </Typography>
            <Stack spacing={1} direction='row' flexWrap={'wrap'} useFlexGap>
                {fertilization.map((item) => (
                    <Stack direction='row' spacing={0.5} key={item.name}>
                        <Typography variant='body2' color='primary' fontWeight={'bold'}>
                            {item.name}:
                        </Typography>
                        <Typography variant='body2' color='primary'>
                            {item.value}
                        </Typography>
                    </Stack>
                ))}
            </Stack>
        </Stack>
    );
};

export const CornDensityRecommendation = ({
    isView,
    isPdf,
    productId,
    field,
    product,
    environments,
    yieldByEnvironment,
    yieldByEnvironmentQueryIsLoading,
    fertilizationCoefficients,
    cornDensityCurve,
    cornDensityCurveQueryIsLoading,
    plantingDate,
    soilMoisture,
    climatePattern,
    environment,
    userNitrogen,
    userDensity,
    successRate,
    updatedAt,
    form,
    recommendation,
    unitProviderProps,
    userNode,
    readOnly,
}: {
    isView: boolean;
    isPdf: boolean;
    id: number;
    productId: number;
    field: ReturnType<typeof useFieldById>['data'];
    product: ReturnType<typeof useCatalogProductById>['data'];
    environments: ReturnType<typeof useEnvironments>['data'];
    cornDensityCurve: ReturnType<typeof useCornDensityCurveForProducts>['data'];
    cornDensityCurveQueryIsLoading: boolean;
    fertilizationCoefficients: ReturnType<typeof useFertilizationCoefficientsForProducts>['data'];
    yieldByEnvironment: ReturnType<typeof useYieldByEnvironmentForProducts>['data'];
    yieldByEnvironmentQueryIsLoading: boolean;
    plantingDate: string;
    soilMoisture: number | undefined;
    climatePattern: number;
    updatedAt: string;
    environment: number;
    userNitrogen: number;
    userDensity: number;
    successRate: number;
    form?: UseFormReturn<DensityRecommendationForm>;
    recommendation?: Recommendation;
    unitProviderProps?: any;
    userNode: ReturnType<typeof useGetUserNode>['data'];
    readOnly: boolean;
}) => {
    const navigate = useNavigate();

    const units = useUnitProvider(unitProviderProps);
    const [userDensityModalOpen, setUserDensityModalOpen] = useState(false);

    const climatePatternChoices = getClimatePatternChoices();
    const recommendationDetails = [
        {
            name: _('Superficie'),
            value: units.toArea(field?.area, 2).toString(),
        },
        {
            name: _('Fecha de siembra'),
            value: formatDate(new Date(plantingDate)),
        },
        // Some locations have no soil moisture data at all (0% is a valid value).
        ...(soilMoisture !== undefined && soilMoisture !== null
            ? [
                  {
                      name: _('Humedad'),
                      value: `${soilMoisture}%`,
                  },
              ]
            : []),
        {
            name: _('Año climático'),
            value: climatePatternChoices.find(({ value }) => value === climatePattern)?.label,
        },
    ];

    const productCornDensityCurve = useMemo(() => cornDensityCurve?.[productId], [cornDensityCurve, productId]);
    const productFertilizationCoefficients = useMemo(
        () => fertilizationCoefficients?.[productId],
        [fertilizationCoefficients, productId]
    );
    const productYieldByEnvironment = useMemo(() => yieldByEnvironment?.[productId], [yieldByEnvironment, productId]);
    const { isEnvironmentTooHigh, isEnvironmentTooLow } = useMemo(() => {
        return {
            isEnvironmentTooHigh:
                environment !== undefined && environments && environment > environments.percentiles.at(-1),
            isEnvironmentTooLow: environment !== undefined && environments && environment < environments.percentiles[0],
        };
    }, [environment, environments]);
    const harvestDensity = productCornDensityCurve?.recommended?.density * 10000;
    const harvestYield = productCornDensityCurve?.recommended?.yield;

    const { density: plantingDensity, bagsPerHectare } = getCornPlantingDensity(
        harvestDensity,
        successRate,
        product?.seedsPerBag
    );
    const bagsPerArea = bagsPerHectare ? units.toBags(bagsPerHectare, 2) : '-';

    const createOrUpdateCropMonitoring = useMutateCreateOrUpdateCropMonitoring(
        {
            environment: environment,
            field: field?.id,
            plantingDate: plantingDate,
            product: productId,
            technology: undefined,
        },
        {
            onSuccess: () => {
                navigate(
                    reverse('fields.FieldDetail', {
                        params: { fieldId: field?.id },
                        qs: { tab: 'recommendations' },
                    })
                );
            },
        }
    );
    const startCampaign = async () => {
        createOrUpdateCropMonitoring.trigger();
    };

    return (
        <>
            <Stack spacing={6}>
                <Grid2 container>
                    <Grid2
                        xs={12}
                        {...(isView
                            ? { md: 6, pb: { xs: 4, md: 0 }, pr: { xs: 0, md: 4 } }
                            : {
                                  lg: 6,
                                  pb: { xs: 4, lg: 0 },
                                  pr: { xs: 0, lg: 4 },
                              })}
                    >
                        <Stack spacing={4}>
                            <Typography variant='subtitle1' color='primary' fontWeight={'bold'}>
                                {_('Ajusta la recomendación según el manejo del productor')}
                            </Typography>
                            <Stack>
                                <Typography variant='subtitle2' color='primary' fontWeight={'bold'}>
                                    {_('Porcentaje de logro')}
                                </Typography>
                                <Stack>
                                    {!isPdf && form ? (
                                        <TextField
                                            type='number'
                                            {...form.register('successRate', {
                                                required: _('Este campo es requerido.'),
                                                min: {
                                                    value: 0,
                                                    message: _('El porcentaje debe ser mayor a {{value}}', {
                                                        value: 0,
                                                    }),
                                                },
                                                max: {
                                                    value: 100,
                                                    message: _('El porcentaje debe ser menor a {{value}}', {
                                                        value: 100,
                                                    }),
                                                },
                                                valueAsNumber: true,
                                            })}
                                            error={!!form.formState.errors.successRate}
                                            helperText={
                                                form.formState.errors.successRate?.message ||
                                                _('% de plantas que suelen nacer con éxito')
                                            }
                                            InputProps={{
                                                endAdornment: <InputAdornment position='end'>%</InputAdornment>,
                                            }}
                                            disabled={readOnly}
                                        />
                                    ) : (
                                        <TextField
                                            type='number'
                                            value={successRate}
                                            disabled
                                            InputProps={{
                                                endAdornment: <InputAdornment position='end'>%</InputAdornment>,
                                            }}
                                            helperText={_('% de plantas que suelen nacer con éxito')}
                                        />
                                    )}
                                </Stack>
                            </Stack>
                            <Stack>
                                <Typography variant='subtitle2' color='primary' fontWeight={'bold'}>
                                    {_('Ambiente del lote')}
                                </Typography>

                                {environments && (
                                    <EnvironmentSliderV2
                                        value={environment}
                                        percentiles={environments.percentiles}
                                        min={environments.min}
                                        max={environments.max}
                                        onChange={(e, value) => {
                                            if (form) {
                                                form.setValue('environment', value);
                                            }
                                        }}
                                        disabled={readOnly}
                                        valueLabelDisplay={isPdf ? 'on' : 'auto'}
                                        unitProviderProps={unitProviderProps}
                                    />
                                )}
                            </Stack>

                            {environments &&
                                !productCornDensityCurve?.recommended &&
                                !cornDensityCurveQueryIsLoading && (
                                    <Alert severity='danger'>
                                        {_(
                                            'No se encontró información para este producto en este ambiente. Intenta con otro ambiente o producto.'
                                        )}
                                    </Alert>
                                )}

                            {isEnvironmentTooHigh && (
                                <Alert severity='danger'>
                                    {_(
                                        'Revisá si el ambiente seleccionado representa las condiciones del lote para obtener una recomendación más precisa.'
                                    )}
                                </Alert>
                            )}
                            {isEnvironmentTooLow && (
                                <Alert severity='danger'>
                                    {_(
                                        'Revisá si el ambiente seleccionado representa las condiciones del lote. El híbrido tiene potencial para rendir más.'
                                    )}
                                </Alert>
                            )}
                        </Stack>
                        {Boolean(isPdf && userNitrogen && userDensity) && (
                            <Stack mt={4} spacing={1}>
                                <Typography variant='subtitle1' color='primary' fontWeight={'bold'}>
                                    {_('Densidad habitual')}
                                </Typography>
                                <UserDensityTable
                                    densityValue={harvestDensity}
                                    nitrogenValue={
                                        productFertilizationCoefficients?.n?.recommended ||
                                        productFertilizationCoefficients?.n?.common
                                    }
                                    yieldValue={harvestYield}
                                    userYieldValue={productYieldByEnvironment?.yield}
                                    isLoading={yieldByEnvironmentQueryIsLoading}
                                    userNitrogen={userNitrogen}
                                    userDensity={userDensity}
                                    unitProviderProps={unitProviderProps}
                                />
                            </Stack>
                        )}
                    </Grid2>
                    <Grid2
                        xs
                        md
                        sx={[
                            {
                                boxShadow: '0px 4px 4px 0px rgba(0, 0, 0, 0.25)',
                                border: (t) => `1px solid ${t.palette.divider}`,
                                padding: 4,
                            },
                        ]}
                    >
                        <Stack spacing={4}>
                            <Stack spacing={1.5}>
                                <Typography variant='h5' color='primary'>
                                    {_('Recomendación para {{field}} de {{grower}}', {
                                        grower: userNode?.name,
                                        field: field?.name,
                                    })}
                                </Typography>
                                <Stack direction='row' spacing={1} flexWrap={'wrap'} useFlexGap>
                                    {recommendationDetails.map((item) => (
                                        <Stack direction='row' spacing={1} key={item.name}>
                                            <Typography
                                                variant='body2'
                                                color='primary.mui.inactive'
                                                fontWeight={'bold'}
                                            >
                                                {item.name}:
                                            </Typography>
                                            <Typography variant='body2' color='primary.mui.inactive'>
                                                {item.value}
                                            </Typography>
                                        </Stack>
                                    ))}
                                </Stack>
                            </Stack>

                            <Divider />

                            <Stack spacing={2}>
                                <Stack direction='row' spacing={3}>
                                    <ValueCard
                                        title={_('Densidad de siembra')}
                                        value={units.toCornDensity(plantingDensity, 2, 'planting').toString()}
                                        loading={!plantingDensity}
                                    />
                                    <ValueCard
                                        title={_('Densidad Objetivo')}
                                        value={units.toCornDensity(harvestDensity, 2).toString()}
                                        loading={!harvestDensity}
                                    />
                                </Stack>

                                {!isPdf && (
                                    <Link
                                        component='button'
                                        color='secondary'
                                        onClick={() => setUserDensityModalOpen(true)}
                                        disabled={!harvestYield}
                                        sx={{
                                            '&[disabled]': {
                                                color: (t) => t.palette.action.disabled,
                                                textDecoration: 'none',
                                                pointerEvents: 'none',
                                            },
                                            alignSelf: 'start',
                                        }}
                                    >
                                        {_('Comparar con la densidad habitual')}
                                    </Link>
                                )}
                            </Stack>
                            <Stack spacing={2}>
                                <ValueCard
                                    title={_('Rendimiento esperado')}
                                    value={units.toCornYield(harvestYield, 2).toString()}
                                    loading={!harvestYield}
                                />
                                <FertilizationCard fertilizationCoefficients={productFertilizationCoefficients} />
                            </Stack>
                            <Stack spacing={1}>
                                <ValueCard
                                    title={_('Bolsas necesarias')}
                                    value={bagsPerArea?.toString() || '-'}
                                    loading={cornDensityCurveQueryIsLoading}
                                />
                            </Stack>

                            {!isPdf && !readOnly && (
                                <Stack direction='row' spacing={1}>
                                    {isView && field?.cropMonitoring.filter((cm) => cm.active).length === 0 && (
                                        <Button
                                            variant='outlined'
                                            color='primary'
                                            onClick={() => {
                                                startCampaign();
                                            }}
                                            loading={createOrUpdateCropMonitoring.isMutating}
                                        >
                                            {_('Comenzar campaña')}
                                        </Button>
                                    )}
                                </Stack>
                            )}
                        </Stack>
                    </Grid2>
                </Grid2>
            </Stack>
            {form && (
                <UserDensityModal
                    open={userDensityModalOpen}
                    onClose={() => setUserDensityModalOpen(false)}
                    form={form}
                    userYieldValue={productYieldByEnvironment?.yield}
                    densityValue={harvestDensity}
                    yieldValue={harvestYield}
                    nitrogenValue={
                        productFertilizationCoefficients?.n?.recommended || productFertilizationCoefficients?.n?.common
                    }
                    isLoading={yieldByEnvironmentQueryIsLoading}
                    userNitrogen={userNitrogen}
                    userDensity={userDensity}
                    readOnly={readOnly}
                />
            )}
        </>
    );
};

export const useCornDensityRecommendationQueries = ({
    id,
    fieldId,
    productId,
    plantingDate,
    soilMoisture,
    climatePattern,
    environment,
    userDensity,
    form,
}: {
    id?: number;
    fieldId?: number;
    productId?: number;
    plantingDate?: string;
    soilMoisture?: number;
    climatePattern?: number;
    environment?: number;
    userDensity: number;
    form?: UseFormReturn<DensityRecommendationForm>;
}) => {
    const country = useSelector(getSessionCountry);
    const fieldQuery = useFieldById({
        id: Number(fieldId),
    });
    const productQuery = useCatalogProductById({
        id: Number(productId),
        crop: 'corn',
    });
    const environmentsQuery = useEnvironments(
        {
            lat: fieldQuery.data?.lat,
            lng: fieldQuery.data?.lng,
            countryCode: country?.code,
            plantingDate,
            climatePattern,
            soilMoisture,
            crop: 'corn',
            irrigation: undefined,
            soilType: undefined,
        },
        {
            onSuccess: (data) => {
                if (form?.getValues('environment') === 0) form?.setValue('environment', data.default);
            },
        }
    );

    const debouncedEnvironment = useDebounce(environment, 500);
    const debouncedUserDensity = useDebounce(userDensity, 1000);

    const cornDensityCurveQuery = useCornDensityCurveForProducts({
        productsId: [productId],
        environment: debouncedEnvironment,
        minValue: 3,
        maxValue: 12,
        step: 0.05,
        crop: 'corn',
    });
    const fertilizationCoefficientsQuery = useFertilizationCoefficientsForProducts({
        environment: debouncedEnvironment,
        products: [
            {
                estimatedYield: cornDensityCurveQuery.data?.[productId]?.recommended?.yield,
                id: productId,
            },
        ],
    });

    const yieldByEnvironmentQuery = useYieldByEnvironmentForProducts({
        environment: debouncedEnvironment,
        productsId: [productId],
        harvestDensity: debouncedUserDensity / 10000,
    });

    const recommendationQuery = useRecommendationById(id);
    const growerQuery = useGetUserNode(fieldQuery.data?.userNode);

    return {
        environmentsQuery,
        cornDensityCurveQuery,
        fertilizationCoefficientsQuery,
        yieldByEnvironmentQuery,
        productQuery,
        fieldQuery,
        recommendationQuery,
        growerQuery,
    };
};

const CornDensityRecommendationWrapper = ({ form: formProp }: { form?: UseFormReturn<DensityRecommendationForm> }) => {
    const localForm = useForm<DensityRecommendationForm>({
        defaultValues: {
            environment: 0,
            successRate: 90,
            userDensity: 0,
            userNitrogen: 0,
        },
        mode: 'all',
    });
    const isView = !formProp;
    const form = formProp || localForm;

    const appInstance = useSelector(getAppInstance);

    // Params
    const params = useParams();
    const id = params.recommendationId ? Number(params.recommendationId) : undefined;

    // Form Values
    const plantingDate = form.watch('plantingDate');
    const productId = form.watch('productId');
    const fieldId = form.watch('fieldId');
    const soilMoisture = form.watch('soilMoisture');
    const climatePattern = form.watch('climatePattern');
    const environment = form.watch('environment');
    const userDensity = form.watch('userDensity');
    const userNitrogen = form.watch('userNitrogen');
    const successRate = form.watch('successRate');
    const formValues = form.watch();

    const debouncedFormValues = useDebounceWithDeepCompare(formValues, 2000);

    // Load Common Data
    const {
        environmentsQuery,
        cornDensityCurveQuery,
        fertilizationCoefficientsQuery,
        yieldByEnvironmentQuery,
        productQuery,
        fieldQuery,
        recommendationQuery,
        growerQuery,
    } = useCornDensityRecommendationQueries({
        id,
        fieldId,
        productId,
        plantingDate,
        soilMoisture,
        climatePattern,
        environment,
        userDensity,
        form,
    });

    // Comments
    const [commentsDrawerOpen, setCommentsDrawerOpen] = useState(false);
    const { shouldShowCommentsButton } = useRecommendationCommentsPermissions(
        recommendationQuery.data?.commentsStatus,
        recommendationQuery.data?.userNode
    );

    const props = {
        isView: isView,
        isPdf: false,
        id: id,
        productId: productId,
        field: fieldQuery.data,
        product: productQuery.data,
        environments: environmentsQuery.data,
        cornDensityCurve: cornDensityCurveQuery.data,
        cornDensityCurveQueryIsLoading: cornDensityCurveQuery.isLoading,
        fertilizationCoefficients: fertilizationCoefficientsQuery.data,
        yieldByEnvironment: yieldByEnvironmentQuery.data,
        yieldByEnvironmentQueryIsLoading: yieldByEnvironmentQuery.isLoading,
        plantingDate: plantingDate,
        soilMoisture: soilMoisture,
        climatePattern: climatePattern,
        form: form,
        updatedAt: recommendationQuery.data?.updated,
        environment: environment,
        userNitrogen: userNitrogen,
        userDensity: userDensity,
        successRate: successRate,
        recommendation: recommendationQuery.data,
        userNode: growerQuery.data,
    };

    // Load/Update Recommendation
    const updateRecommendationMutation = useUpdateRecommendationMutation();
    const [recommendationIsLoaded, setRecommendationIsLoaded] = useState(false);
    useEffect(() => {
        if (recommendationQuery.data && !recommendationIsLoaded) {
            form.reset({
                environment: recommendationQuery.data.cornDensity?.environment,
                successRate: recommendationQuery.data.cornDensity?.userPlanterEfficiency,
                userDensity: recommendationQuery.data.cornDensity?.userDensity,
                userNitrogen: recommendationQuery.data.cornDensity?.userNitrogen,
                plantingDate: recommendationQuery.data.cornDensity?.plantingDate,
                productId: recommendationQuery.data.cornDensity?.productProfile,
                fieldId: recommendationQuery.data.field,
                soilMoisture: recommendationQuery.data.cornDensity?.soilMoisture,
                climatePattern: recommendationQuery.data.cornDensity?.climatePattern,
            });
            setRecommendationIsLoaded(true);
        }
    }, [recommendationQuery.data, recommendationIsLoaded]);
    useEffect(() => {
        const currentSavedValues = {
            environment: recommendationQuery.data?.cornDensity?.environment,
            successRate: recommendationQuery.data?.cornDensity?.userPlanterEfficiency,
            userDensity: recommendationQuery.data?.cornDensity?.userDensity,
            userNitrogen: recommendationQuery.data?.cornDensity?.userNitrogen,
            plantingDate: recommendationQuery.data?.cornDensity?.plantingDate,
            productId: recommendationQuery.data?.cornDensity?.productProfile,
            fieldId: recommendationQuery.data?.field,
            soilMoisture: recommendationQuery.data?.cornDensity?.soilMoisture,
            climatePattern: recommendationQuery.data?.cornDensity?.climatePattern,
        };

        if (
            id &&
            recommendationIsLoaded &&
            !form.formState.errors.environment &&
            !form.formState.errors.successRate &&
            !form.formState.errors.userDensity &&
            !form.formState.errors.userNitrogen &&
            JSON.stringify(currentSavedValues) !== JSON.stringify(debouncedFormValues)
        ) {
            updateRecommendationMutation.trigger({
                id,
                status: 'saved',
                field: fieldQuery.data?.id,
                appInstance: appInstance.id,
                corn_density: {
                    plantingDate: plantingDate,
                    product_profile: productId,
                    climate_pattern: climatePattern,
                    soil_moisture: soilMoisture,
                    irrigation: undefined,
                    soilType: undefined,
                    environment: form.getValues('environment'),
                    recommended_environment: environmentsQuery.data?.default,
                    user_density: form.getValues('userDensity'),
                    user_nitrogen: form.getValues('userNitrogen'),
                    user_planter_efficiency: form.getValues('successRate'),
                },
                backup: {
                    ...props.recommendation,
                    otherData: {
                        field: props.field,
                        product: props.product,
                        environments: props.environments,
                        cornDensityCurve: props.cornDensityCurve,
                        cornDensityCurveQueryIsLoading: props.cornDensityCurveQueryIsLoading,
                        fertilizationCoefficients: props.fertilizationCoefficients,
                        yieldByEnvironment: props.yieldByEnvironment,
                        yieldByEnvironmentQueryIsLoading: props.yieldByEnvironmentQueryIsLoading,
                        userNode: props.userNode,
                    },
                },
            });
        }
    }, [id, debouncedFormValues]);

    const breadcrumbs = useBreadcrumbs('recommendation.densityRecommendationDetail');
    const isSmOrLower = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
    const canEditResource = useHasWriteAccess();
    const readOnly = !canEditResource;

    // Soil moisture is not required: some locations have no data for it.
    if (!plantingDate || !climatePattern || fieldQuery.isLoading || !productId || !environment) {
        return (
            <Stack alignItems={'center'} justifyContent={'center'} height={'100%'}>
                <Spinner />
            </Stack>
        );
    }

    return (
        <>
            {isView ? (
                <PageContainer
                    heroHeader={
                        <Header
                            variant='full-gray'
                            title={_('Recomendación de densidad de {{hybridName}}', {
                                hybridName: productQuery.data?.name,
                            })}
                            subtitle={
                                props.updatedAt
                                    ? _('Última actualización {{date}} {{time}}', {
                                          date: formatDate(new Date(props.updatedAt)),
                                          time: new Date(props.updatedAt).toLocaleTimeString(),
                                      })
                                    : undefined
                            }
                            actions={[
                                <RecommendationShare
                                    key='recommendation-share'
                                    recommendation={{
                                        ...props.recommendation,
                                        otherData: {
                                            field: props.field,
                                            product: props.product,
                                            environments: props.environments,
                                            cornDensityCurve: props.cornDensityCurve,
                                            cornDensityCurveQueryIsLoading: props.cornDensityCurveQueryIsLoading,
                                            fertilizationCoefficients: props.fertilizationCoefficients,
                                            yieldByEnvironment: props.yieldByEnvironment,
                                            yieldByEnvironmentQueryIsLoading: props.yieldByEnvironmentQueryIsLoading,
                                            userNode: props.userNode,
                                        },
                                    }}
                                    type='density'
                                    version={2}
                                />,
                                shouldShowCommentsButton &&
                                    (isSmOrLower ? (
                                        <Button
                                            variant='contained'
                                            color='primary'
                                            fullWidth
                                            onClick={() => setCommentsDrawerOpen(true)}
                                        >
                                            {_('Ver comentarios')}
                                        </Button>
                                    ) : (
                                        <RecommendationCommentsButton
                                            key='comments-button'
                                            commentsStatus={recommendationQuery.data?.commentsStatus}
                                            onOpenDrawer={() => setCommentsDrawerOpen(true)}
                                        />
                                    )),
                            ].filter(Boolean)}
                            breadcrumbs={breadcrumbs}
                        />
                    }
                >
                    <Stack pt={6}>
                        <CornDensityRecommendation {...props} readOnly={readOnly} />
                    </Stack>
                </PageContainer>
            ) : (
                <Container
                    maxWidth='xl'
                    sx={{
                        padding: '0 !important',
                        paddingBottom: '8px !important',
                    }}
                >
                    <CornDensityRecommendation {...props} readOnly={readOnly} />
                </Container>
            )}
            {id && (
                <RecommendationCommentsDrawer
                    open={commentsDrawerOpen}
                    setCommentsDrawerOpen={setCommentsDrawerOpen}
                    recommendationId={id}
                />
            )}
        </>
    );
};

export default CornDensityRecommendationWrapper;
